import Rest from '../Rest'

/**
 * Ingressos: validação na portaria, painel de check-in e validadores designados.
 *
 * `validate` responde **200 mesmo recusando a entrada** — o veredito vem em
 * `result` (VALID, ALREADY_CHECKED_IN, NOT_FOUND…). Quem tratar recusa como erro
 * de rede vai mostrar "falha de conexão" para um ingresso que simplesmente já foi
 * usado. Ver docs/TICKETS_API.md no backend.
 */
export default class TicketService extends Rest {
  /**
   * @type {String}
   */
  static resource = '/api/v1/tickets'

  static build(vs: any = null) {
    return new this(this.resource, null, vs)
  }

  /** Eventos que o usuário logado pode validar. */
  getValidatableItems() {
    return this.get('/items')
  }

  /**
   * `result` também pode vir `AMBIGUOUS`: o CPF tem sessão hoje em mais de um
   * evento deste validador, **nada foi registrado** e a escolha vai em
   * `candidates`. Reenviar com `reference_type`/`reference_id` +
   * `scheduled_event_id` fecha a validação na ocorrência escolhida.
   *
   * @param payload { credential_type: 'token'|'code'|'cpf', credential, reference_type?,
   *                  reference_id?, scheduled_event_id?, channel?, force?, idempotency_key? }
   */
  validate(payload: any) {
    return this.post('/validate', payload)
  }

  /** Consulta sem consumir — a tela aberta pelo QR. */
  lookup(token: string) {
    return this.get(`/lookup/${encodeURIComponent(token)}`)
  }

  undoCheckIn(checkInId: number) {
    return this.post(`/check_ins/${checkInId}/undo`, {})
  }

  revoke(ticketId: number, reason: string) {
    return this.post(`/${ticketId}/revoke`, { reason })
  }

  summary(referenceType: string, referenceId: number, date: string = '') {
    return this.get(`/summary?${this.itemQuery(referenceType, referenceId, date)}`)
  }

  list(referenceType: string, referenceId: number, date: string = '', perPage: number = 50) {
    return this.get(`?${this.itemQuery(referenceType, referenceId, date)}&per_page=${perPage}`)
  }

  getValidators(referenceType: string, referenceId: number) {
    return this.get(`/validators?${this.itemQuery(referenceType, referenceId)}`)
  }

  /**
   * Busca escopada de quem pode ser designado.
   *
   * Substitui `/suggestion/user`, que respondia com a tabela `users` inteira a
   * qualquer autenticado — um clube digitando "ana" recebia as Anas da
   * plataforma inteira, clientes de concorrentes incluídos. Aqui só há duas
   * portas: e-mail exato, ou nome de quem já trabalha para o clube.
   */
  searchValidatorCandidates(term: string) {
    return this.get(`/validators/search?term=${encodeURIComponent(term)}`)
  }

  /**
   * @param target { user_id? } ou { email, name? } — o e-mail aceita quem ainda
   *               não tem conta e vira convite.
   * @param applyToAll designa em todos os eventos com ingresso do estabelecimento.
   */
  addValidator(referenceType: string, referenceId: number, target: any, applyToAll = false) {
    return this.post('/validators', {
      reference_type: referenceType,
      reference_id: referenceId,
      apply_to_all: applyToAll,
      ...target
    })
  }

  removeValidator(validatorId: number) {
    return this.delete(`/validators/${validatorId}`)
  }

  private itemQuery(referenceType: string, referenceId: number, date: string = '') {
    const query = `reference_type=${encodeURIComponent(referenceType)}&reference_id=${referenceId}`

    return date ? `${query}&date=${date}` : query
  }
}
